import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from './Toast';
import './EmployeeFormModal.css';

const DEPARTMENTS = ['IT', 'HR', 'Finance', 'Operations', 'Construction', 'Sales', 'Management'];

function EmployeeFormModal({ employee, onClose, onSuccess }) {
  const isEdit = !!employee;

  const [form, setForm] = useState({
    name: '',
    email: '',
    employeeCode: '',
    joinDate: new Date().toISOString().split('T')[0],
    designation: '',
    department: '',
    baseSalary: '',
    bankAccount: '',
    active: true,
    usesSystem: false,
    role: 'EMPLOYEE',
    password: '',
    branchId: '',
    managerId: '',
  });

  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchEmployees, setBranchEmployees] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    axios.get('/company/branches').then(res => {
      const data = res.data?.data || res.data || [];
      setBranches(Array.isArray(data) ? data : []);
    }).catch(() => {});

    // Load roles from DB; fall back to system defaults
    axios.get('/roles').then(res => {
      const data = res.data?.data || res.data || [];
      const active = Array.isArray(data) ? data.filter(r => r.active) : [];
      setRoles(active.length > 0 ? active : [
        { id: 'EMPLOYEE', name: 'EMPLOYEE' },
        { id: 'SUPERVISOR', name: 'SUPERVISOR' },
        { id: 'BRANCH_MANAGER', name: 'BRANCH_MANAGER' },
      ]);
    }).catch(() => {
      setRoles([
        { id: 'EMPLOYEE', name: 'EMPLOYEE' },
        { id: 'SUPERVISOR', name: 'SUPERVISOR' },
        { id: 'BRANCH_MANAGER', name: 'BRANCH_MANAGER' },
      ]);
    });
  }, []);

  // Fetch employees for the selected branch whenever branchId changes
  useEffect(() => {
    if (!form.branchId) {
      setBranchEmployees([]);
      return;
    }
    axios.get('/employees', { params: { branchId: form.branchId, size: 200 } })
      .then(res => {
        const data = res.data?.employees || res.data?.data || [];
        setBranchEmployees(Array.isArray(data) ? data : []);
      })
      .catch(() => setBranchEmployees([]));
  }, [form.branchId]);

  useEffect(() => {
    if (isEdit) {
      let fullPhone = employee.phone || '';
      let code = '91';
      let num = '';
      if (fullPhone === '91' || !fullPhone) {
        code = '91';
        num = '';
      } else if (fullPhone.startsWith('+')) {
        const match = fullPhone.match(/^\+(\d{1,4})/);
        if (match) {
          code = '+' + match[1];
          num = fullPhone.slice(match[0].length);
        }
      } else if (fullPhone.startsWith('91') && fullPhone.length > 2) {
        code = '91';
        num = fullPhone.slice(2);
      } else {
        code = '91';
        num = fullPhone;
      }
      setCountryCode(code);
      setPhoneNumber(num);

      setForm({
        name: employee.userName || employee.name || '',
        email: employee.userEmail || employee.email || '',
        employeeCode: employee.employeeCode || '',
        joinDate: employee.joinDate || '',
        designation: employee.designation || '',
        department: employee.department || '',
        baseSalary: employee.baseSalary || '',
        bankAccount: employee.bankAccount || '',
        active: employee.active !== undefined ? employee.active : true,
        usesSystem: employee.hasLogin || false,
        role: employee.customRoleName || employee.userRole || 'EMPLOYEE',
        password: '',
        branchId: employee.branchId ? String(employee.branchId) : '',
        managerId: employee.managerId ? String(employee.managerId) : '',
      });
    } else {
      setCountryCode('91');
      setPhoneNumber('');
    }
  }, [employee, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === 'checkbox' ? checked : value;
    if (['phone', 'alternatePhone'].includes(name) && typeof val === 'string') {
      val = val.replace(/[a-zA-Z]/g, '');
    }
    setForm(prev => {
      const updated = { ...prev, [name]: val };
      if (name === 'branchId') updated.managerId = '';
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.joinDate) errs.joinDate = 'Join date is required';
    if (!form.baseSalary || parseFloat(form.baseSalary) < 0) errs.baseSalary = 'Valid salary required';
    if (form.usesSystem && !form.email.trim()) {
      errs.email = 'Email is required for system login';
    } else if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Invalid email format';
    }
    const combinedPhone = phoneNumber ? `${countryCode}${phoneNumber}` : '';
    if (combinedPhone && !/^[0-9+\-\s()]{7,20}$/.test(combinedPhone)) {
      errs.phone = 'Invalid phone number';
    }
    if (!isEdit && !form.branchId && form.role !== 'COMPANY_ADMIN') errs.branchId = 'Branch is required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const combinedPhone = phoneNumber ? `${countryCode}${phoneNumber}` : '';

    try {
      if (isEdit) {
        await axios.put(`/employees/${employee.id}`, {
          name: form.name,
          email: form.email,
          phone: combinedPhone || null,
          joinDate: form.joinDate,
          designation: form.designation,
          department: form.department,
          baseSalary: parseFloat(form.baseSalary),
          bankAccount: form.bankAccount,
          active: form.active,
          branchId: form.branchId ? parseInt(form.branchId) : null,
          managerId: form.managerId ? parseInt(form.managerId) : null,
          role: form.usesSystem ? form.role : null,
        });

        if (form.usesSystem && !employee.hasLogin) {
          // Grant system access to existing employee
          const res = await axios.post(`/employees/${employee.id}/grant-access`, {
            email: form.email,
            role: form.role,
          });
          const data = res.data?.data || res.data;
          setCreatedResult({
            name: form.name,
            email: form.email,
            plainPassword: data.tempPassword || data.plainPassword || '(auto-generated — check employee record)',
          });
          return; // Modal stays open showing generated password screen
        } else if (!form.usesSystem && employee.hasLogin && employee.userId) {
          // Deactivate linked user account
          await axios.put(`/users/${employee.userId}/deactivate`);
        } else if (form.usesSystem && employee.hasLogin && employee.userId) {
          // Activate linked user account
          await axios.put(`/users/${employee.userId}/activate`);
        }
        onSuccess();
      } else if (form.usesSystem) {
        const res = await axios.post('/employees/create-with-user', {
          name: form.name,
          email: form.email,
          phone: combinedPhone || null,
          employeeCode: form.employeeCode || null,
          joinDate: form.joinDate,
          designation: form.designation,
          department: form.department,
          baseSalary: parseFloat(form.baseSalary) || 0,
          bankAccount: form.bankAccount,
          role: form.role,
          password: form.password || null,
          branchId: form.branchId || null,
          managerId: form.managerId || null,
        });
        const data = res.data?.data || res.data;
        setCreatedResult({
          name: data.employee?.userName || form.name,
          email: form.email,
          plainPassword: data.plainPassword || data.employee?.plainPassword,
        });
      } else {
        await axios.post('/employees/create', {
          name: form.name,
          email: form.email || null,
          phone: combinedPhone || null,
          employeeCode: form.employeeCode || null,
          joinDate: form.joinDate,
          designation: form.designation,
          department: form.department,
          baseSalary: parseFloat(form.baseSalary) || 0,
          bankAccount: form.bankAccount,
          branchId: form.branchId || null,
          managerId: form.managerId || null,
        });
        onSuccess();
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message
        || (typeof data?.error === 'string' ? data.error : data?.error?.message)
        || 'Failed to save employee';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ── Success screen after create-with-user ─────────────────────────────────
  if (createdResult) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Employee Created</h2>
            <button className="close-button" onClick={() => { onSuccess(); onClose(); }}>&times;</button>
          </div>
          <div style={{ padding: '1rem 0' }}>
            <p style={{ marginBottom: '1rem', color: '#374151' }}>
              Share these login credentials with <strong>{createdResult.name}</strong>. They will be asked to set a new password on first login.
            </p>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Email</span>
                <span style={{ fontWeight: 500 }}>{createdResult.email}</span>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Temporary Password</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <code style={{ background: '#e5e7eb', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '1rem', letterSpacing: '0.05em', fontWeight: 600 }}>
                    {createdResult.plainPassword || '(auto-generated — check employee record)'}
                  </code>
                  {createdResult.plainPassword && (
                    <button
                      onClick={() => copyToClipboard(createdResult.plainPassword)}
                      style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', color: '#374151' }}
                    >
                      Copy
                    </button>
                  )}
                </div>
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
              This password will not be shown again after you close this dialog.
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={() => { onSuccess(); onClose(); }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Create / Edit form ─────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Employee' : 'Add Employee'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name *</label>
              <input name="name" value={form.name} onChange={handleChange}
                placeholder="John Doe" className={errors.name ? 'error' : ''} required />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>Employee Code</label>
              <input name="employeeCode" value={form.employeeCode} onChange={handleChange}
                placeholder="Auto-generated if blank" disabled={isEdit} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email {form.usesSystem ? '*' : ''}</label>
              <input name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="john@company.com" className={errors.email ? 'error' : ''} required={form.usesSystem} />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Phone</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                  className={`form-input${errors.phone ? ' error' : ''}`}
                  style={{ width: '80px', flexShrink: 0 }}
                  maxLength="5"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                  className={`form-input${errors.phone ? ' error' : ''}`}
                  style={{ flexGrow: 1 }}
                  maxLength="15"
                />
              </div>
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Join Date *</label>
              <input name="joinDate" type="date" value={form.joinDate} onChange={handleChange}
                className={errors.joinDate ? 'error' : ''} required />
              {errors.joinDate && <span className="error-message">{errors.joinDate}</span>}
            </div>
            <div className="form-group">
              <label>Base Salary *</label>
              <input name="baseSalary" type="number" value={form.baseSalary} onChange={handleChange}
                placeholder="0.00" step="0.01" min="0" className={errors.baseSalary ? 'error' : ''} required />
              {errors.baseSalary && <span className="error-message">{errors.baseSalary}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Designation</label>
              <input name="designation" value={form.designation} onChange={handleChange}
                placeholder="e.g. Site Engineer" />
            </div>
            <div className="form-group">
              <label>Department</label>
              <select name="department" value={form.department} onChange={handleChange}>
                <option value="">Select Department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Bank Account</label>
            <input name="bankAccount" value={form.bankAccount} onChange={handleChange}
              placeholder="Account number" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Branch {form.role !== 'COMPANY_ADMIN' ? '*' : '(N/A for Company Admin)'}</label>
              <select name="branchId" value={form.branchId} onChange={handleChange}
                className={errors.branchId ? 'error' : ''}
                disabled={form.role === 'COMPANY_ADMIN'}
                required={form.role !== 'COMPANY_ADMIN'}>
                <option value="">{form.role === 'COMPANY_ADMIN' ? 'Company-wide access' : 'Select Branch'}</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {errors.branchId && <span className="error-message">{errors.branchId}</span>}
              {form.role === 'COMPANY_ADMIN' && (
                <small style={{color: '#666'}}>Company admins have access to all branches</small>
              )}
            </div>
            <div className="form-group">
              <label>Reporting To</label>
              <select name="managerId" value={form.managerId} onChange={handleChange}
                disabled={!form.branchId || form.role === 'COMPANY_ADMIN'}>
                <option value="">{form.branchId && form.role !== 'COMPANY_ADMIN' ? 'None' : form.role === 'COMPANY_ADMIN' ? 'N/A' : 'Select a branch first'}</option>
                {branchEmployees
                  .filter(emp => !employee || emp.id !== employee.id)
                  .map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.userName || emp.name} {emp.employeeCode ? `(${emp.employeeCode})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {isEdit && (
            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
                <span>Active</span>
              </label>
            </div>
          )}

          {/* System login toggle — shown always */}
          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" name="usesSystem" checked={form.usesSystem} onChange={handleChange} />
              <span>This employee will log in to the system</span>
            </label>
          </div>

          {/* Login account details */}
          {form.usesSystem && (
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '1rem', marginTop: '0.5rem' }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#0369a1' }}>Login account details</p>
              <div className="form-row">
                <div className="form-group">
                  <label>Role</label>
                  <select name="role" value={form.role} onChange={handleChange}>
                    {roles.map(r => (
                      <option key={r.id || r.name} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>
                {/* Only show password input if they don't have login yet (create mode or edit mode where access was not yet granted) */}
                {(!isEdit || !employee.hasLogin) && (
                  <div className="form-group">
                    <label>Password (leave blank to auto-generate)</label>
                    <input name="password" type="password" value={form.password} onChange={handleChange}
                      placeholder="Auto-generated if blank" autoComplete="new-password" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add Employee')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmployeeFormModal;
